import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLoading } from './LoadingContext';

const DesignContext = createContext();
const BOOTSTRAP_FETCH_TIMEOUT_MS = 8000;
const DESIGN_CACHE_KEY = 'viverra_design_cache';

export const useDesign = () => {
  const context = useContext(DesignContext);
  if (!context) {
    throw new Error('useDesign must be used within a DesignProvider');
  }
  return context;
};

const getDefaultDesignConfig = () => ({
    // Branding
    site_name: 'Viverra',
    site_tagline: 'Your trusted shopping destination',
    logo_text: 'V',
    about_text: 'Viverra brings you a curated collection of elegant fashion, designer bags, and jewelry accessories.',
    
    // Colors
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#EC4899',
    background_color: '#F9FAFB',
    text_color: '#111827',
    header_bg: '#FFFFFF',
    footer_bg: '#1F2937',
    footer_text: '#F9FAFB',
    
    // Hero
    hero_title: 'Luxury E-Commerce',
    hero_subtitle: 'Discover premium products, exclusive collections, and unmatched quality',
    hero_button_text: 'Explore Collection',
    
    // Features
    feature_1_title: 'Lightning Fast Shipping',
    feature_1_description: 'Express delivery worldwide',
    feature_2_title: 'Bank-Level Security',
    feature_2_description: 'Advanced encryption',
    feature_3_title: 'Premium Support',
    feature_3_description: '24/7 customer service',
    
    // Footer
    footer_about: 'Elegant fashion, designer bags, and jewelry - curated for everyday and special occasions.',
    footer_copyright: '',
    contact_email: '',
    contact_phone: '',
    social_facebook: '',
    social_twitter: '',
    social_instagram: '',
    social_linkedin: '',
    whatsapp_number: '',
    whatsapp_message: 'Hi Viverra, I have a question about a product.',

    // Payment & Delivery
    // Stored/read as the strings 'true'/'false' like every other toggle in this file
    // (e.g. special_offer_enabled) - system_settings values always round-trip as strings.
    cod_enabled: 'true',
    pickup_enabled: 'false',

    // Home: Special Offer Banner
    special_offer_banner_image: '',
    special_offer_label: 'SPECIAL OFFER',
    special_offer_percent_text: '10%',
    special_offer_off_text: 'OFF',
    special_offer_button_text: 'Shop Now',
    special_offer_button_link: '/products',
    special_offer_enabled: 'true',
    special_offer_autoplay: 'true',
    special_offer_autoplay_speed: '4000',
    special_offer_scroll_direction: 'rtl',
    special_offer_swipe_enabled: 'true',

    hero_banner_autoplay: 'true',
    hero_banner_autoplay_speed: '4000',
    hero_banner_scroll_direction: 'rtl',
    hero_banner_swipe_enabled: 'true',
    hero_left_autoplay: 'true',
    hero_left_autoplay_speed: '4000',
    hero_left_scroll_direction: 'rtl',
    hero_left_swipe_enabled: 'true',
    hero_right_autoplay: 'true',
    hero_right_autoplay_speed: '4000',
    hero_right_scroll_direction: 'rtl',
    hero_right_swipe_enabled: 'true',
    hero_banner_enabled: 'true',
    hero_left_enabled: 'true',
    hero_right_enabled: 'true',

    // Products page hero
    products_hero_enabled: 'true',
    products_hero_use_image: 'false',
    products_hero_background_image: '',
    products_hero_overlay_enabled: 'true',
    products_hero_autoplay: 'true',
    products_hero_autoplay_speed: '4000',
    products_hero_scroll_direction: 'rtl',
    products_hero_swipe_enabled: 'true',
    products_hero_top_label: 'Collection',
    products_hero_title: 'Our Collection',
    products_hero_subtitle: 'Discover our curated selection of elegant fashion, designer bags, and luxury accessories'
});

export const DesignProvider = ({ children }) => {
  const { track } = useLoading();
  const didInitRef = useRef(false);

  const [designConfig, setDesignConfig] = useState(() => {
    try {
      const cached = localStorage.getItem(DESIGN_CACHE_KEY);
      if (cached) return { ...getDefaultDesignConfig(), ...JSON.parse(cached) };
    } catch (e) {}
    return getDefaultDesignConfig();
  });

  // If we have a cached config, start with loading=false (no flash).
  // If no cache yet (first ever visit), start with loading=true until fetch completes.
  const [loading, setLoading] = useState(() => {
    try {
      return !localStorage.getItem(DESIGN_CACHE_KEY);
    } catch (e) {
      return true;
    }
  });

  const fetchWithTimeout = useCallback(async (url, options = {}, timeoutMs = BOOTSTRAP_FETCH_TIMEOUT_MS) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  const fetchDesignConfig = useCallback(async () => {
    // Only show loading state if we have no cached data
    const hasCached = (() => { try { return !!localStorage.getItem(DESIGN_CACHE_KEY); } catch(e) { return false; } })();
    if (!hasCached) setLoading(true);
    try {
      await track(async () => {
        const response = await fetchWithTimeout('/api/settings/design');
        const data = await response.json();
        if (data.success && data.data.design) {
          setDesignConfig(prev => ({ ...prev, ...data.data.design }));
          try { localStorage.setItem(DESIGN_CACHE_KEY, JSON.stringify(data.data.design)); } catch(e) {}
        }
      });
    } catch (error) {
      console.error('Error fetching design config:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchWithTimeout, track]);

  const applyDesignStyles = useCallback(() => {
    const root = document.documentElement;
    
    // Apply CSS variables
    root.style.setProperty('--primary-color', designConfig.primary_color);
    root.style.setProperty('--secondary-color', designConfig.secondary_color);
    root.style.setProperty('--accent-color', designConfig.accent_color);
    root.style.setProperty('--background-color', designConfig.background_color);
    root.style.setProperty('--text-color', designConfig.text_color);
    root.style.setProperty('--header-bg', designConfig.header_bg);
    root.style.setProperty('--footer-bg', designConfig.footer_bg);
    root.style.setProperty('--footer-text', designConfig.footer_text);
  }, [designConfig]);

  useEffect(() => {
    // React 18 StrictMode runs effects twice in dev; avoid duplicate boot fetch.
    if (didInitRef.current) return;
    didInitRef.current = true;
    fetchDesignConfig();
  }, [fetchDesignConfig]);

  useEffect(() => {
    applyDesignStyles();
  }, [applyDesignStyles]);

  const value = {
    designConfig,
    loading,
    refreshDesign: fetchDesignConfig
  };

  return (
    <DesignContext.Provider value={value}>
      {children}
    </DesignContext.Provider>
  );
};

export default DesignContext;



